/**
 * The email incrementality holdout: a fixed 10% of people who never receive a
 * marketing send, so the lift of email can be measured against them.
 *
 * Membership is computed, not stored: `holdoutFlagFor(person)` hashes a
 * stable key with a versioned salt, so the browser, the api and any analysis
 * script agree with no database column, and imported subscribers can be
 * assigned later by the same rule.
 *
 * The key is the person's **email** (trimmed, lowercased), not their
 * Customer.io id. The Customer.io id changes when a lead keyed by email signs
 * up and is re-keyed to `users.id`; hashing it would move people in or out of
 * the holdout at sign-up. The email does not change, so the answer is stable
 * per email address. Without an email the key falls back to an E.164 phone,
 * then `users.id` (see `holdoutKeyFor`).
 *
 * ⚠️ Remaining edge: a person with no email (a phone sign-up, ~17.6% of
 * registrations) who later adds one gets a new key. That does not contaminate
 * the email experiment: nobody can be emailed, or held out of an email,
 * before they have an address. The browser only writes the flag once an
 * email is known, and PostHog's `$set_once` keeps the first value written.
 * A person whose subscribed address differs from their account email can
 * still disagree between surfaces; that needs two addresses on one person.
 *
 * The result is written as the `holdout_flag` person property to PostHog
 * (`$set_once`) and as a Customer.io attribute, where the agency excludes it
 * from every send path.
 *
 * ⛔ Changing the salt, the percentage, the key or the hash reshuffles who is
 * held out, which silently voids every incrementality readout already
 * running. Bump the salt's version only as a deliberate new experiment.
 */
export const HOLDOUT_FLAG = 'holdout_flag'
export const HOLDOUT_SALT = 'cio-holdout:v1:'
export const HOLDOUT_PERCENT = 10

const FNV_OFFSET_BASIS = 0x811c9dc5
const FNV_PRIME = 0x01000193

/**
 * FNV-1a (32-bit) over the UTF-8 bytes of `input`, as an unsigned integer.
 *
 * UTF-8 is encoded by hand rather than with `TextEncoder` so the result is
 * identical in every runtime we run in, including test environments that do
 * not provide it. Lone surrogates are encoded as U+FFFD, matching TextEncoder.
 */
export const fnv1a32 = (input: string): number => {
  let hash = FNV_OFFSET_BASIS
  const mix = (byte: number) => {
    hash ^= byte
    hash = Math.imul(hash, FNV_PRIME) >>> 0
  }
  for (let i = 0; i < input.length; i++) {
    let code = input.charCodeAt(i)
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = input.charCodeAt(i + 1)
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00)
        i++
      } else {
        code = 0xfffd
      }
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      code = 0xfffd
    }
    if (code < 0x80) {
      mix(code)
    } else if (code < 0x800) {
      mix(0xc0 | (code >> 6))
      mix(0x80 | (code & 0x3f))
    } else if (code < 0x10000) {
      mix(0xe0 | (code >> 12))
      mix(0x80 | ((code >> 6) & 0x3f))
      mix(0x80 | (code & 0x3f))
    } else {
      mix(0xf0 | (code >> 18))
      mix(0x80 | ((code >> 12) & 0x3f))
      mix(0x80 | ((code >> 6) & 0x3f))
      mix(0x80 | (code & 0x3f))
    }
  }
  return hash >>> 0
}

const clean = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

const E164 = /^\+[1-9]\d{6,14}$/

export interface HoldoutIdentity {
  email?: string | null
  phone?: string | null
  userId?: string | null
}

/**
 * The key a person's holdout is hashed from: the trimmed, lowercased email
 * when there is one; else an E.164 phone; else `users.id`. `null` when there
 * is nothing to key on.
 *
 * Pass only `{ email }` where a phone or id fallback would lock in a value
 * before the person has an email (the browser does, because PostHog keeps the
 * first value it is given).
 */
export const holdoutKeyFor = ({
  email,
  phone,
  userId,
}: HoldoutIdentity): string | null => {
  const cleanEmail = clean(email)
  if (cleanEmail) return cleanEmail.toLowerCase()
  const cleanPhone = clean(phone)
  if (cleanPhone && E164.test(cleanPhone)) return cleanPhone
  return clean(userId)
}

/** Whether this key (from `holdoutKeyFor`) is in the holdout. */
export const isHoldout = (holdoutKey: string): boolean =>
  fnv1a32(HOLDOUT_SALT + holdoutKey) % 100 < HOLDOUT_PERCENT

/** `holdoutKeyFor` then `isHoldout`; `null` when there is nothing to key on. */
export const holdoutFlagFor = (identity: HoldoutIdentity): boolean | null => {
  const key = holdoutKeyFor(identity)
  return key === null ? null : isHoldout(key)
}
